import { createSlice } from "@reduxjs/toolkit";
import { LayerAlias, UnitTypes, PointClassificationsAliases } from "constant";
import { initialState } from "./initialState";

const combineUnitsList = (oldUnits: any[], newUnits: any[]) => {
  let combinedArray = [...(oldUnits ?? []), ...newUnits].reduce((acc, obj) => {
    const existingEntry = acc[obj.id];
    const isVisible = existingEntry?.isVisible ?? obj.isVisible ?? undefined;
    acc[obj.id] = { ...obj, isVisible };
    return acc;
  }, {});

  combinedArray = Object.values(combinedArray);
  return combinedArray;
};

export const mainSlice = createSlice({
  name: "unitsManager",
  initialState,
  reducers: {
    fetchUnitsRequest: (state) => {
      state.isFetchUnitsLoading = true;
    },
    fetchUnitsSuccess: (state, { payload }) => {
      const { units, total } = payload.data;
      const { alias, type } = payload.params;

      const combinedUnits = combineUnitsList(
        state.unitsCollection[alias][type]?.units,
        units ?? []
      );

      state.unitsCollection[alias][type] = {
        units: combinedUnits,
        total
      };

      state.isFetchUnitsLoading = false;
    },
    fetchUnitsError: (state) => {
      state.isFetchUnitsLoading = false;
    },

    clearUnits: (state, { payload }) => {
      const { alias, type } = payload.params;

      state.unitsCollection[alias][type] = {
        units: [],
        total: 0
      };
    },

    fetchUnitRequest: (state) => {
      state.isFetchUnitsLoading = true;
    },
    fetchUnitSuccess: (state) => {
      state.isFetchUnitsLoading = false;
    },
    fetchUnitError: (state) => {
      state.isFetchUnitsLoading = false;
    },

    createUnitRequest: (state) => {
      state.isUnitCreateLoading = true;
    },
    createUnitSuccess: (state, { payload }) => {
      const { alias, type } = payload.params;

      state.unitsCollection[alias][type].units = [
        ...state.unitsCollection[alias][type].units,
        { ...payload.data }
      ];
      state.unitsCollection[alias][type].total =
        state.unitsCollection[alias][type].total + 1;

      state.isUnitCreateLoading = false;
    },
    createUnitError: (state) => {
      state.isUnitCreateLoading = false;
    },

    updateUnitRequest: (state) => {
      state.isUnitCreateLoading = true;
    },
    updateUnitSuccess: (state, { payload }) => {
      const { alias, type } = payload.params;

      state.unitsCollection[alias][type].units = state.unitsCollection[
        payload.params.alias
      ][payload.params.type].units.map((unit) =>
        String(unit.id) !== String(payload.data.id)
          ? unit
          : { ...unit, ...payload.data }
      );

      state.isUnitCreateLoading = false;
    },
    updateUnitError: (state) => {
      state.isUnitCreateLoading = false;
    },

    deleteUnitRequest: (state) => {
      state.isUnitDeleteLoading = true;
    },
    deleteUnitSuccess: (state, { payload }) => {
      const { alias, type } = payload.params;

      state.isUnitDeleteLoading = false;

      const newUnits = state.unitsCollection[alias][type].units.filter(
        (unit) => String(unit.id) !== String(payload.data.id)
      );

      state.unitsCollection[alias][type].units = newUnits;
      state.unitsCollection[alias][type].total =
        state.unitsCollection[alias][type].total - 1;
    },
    deleteUnitError: (state) => {
      state.isUnitDeleteLoading = false;
    },

    fetchUnitsClassificationsRequest: (state) => {
      state.isUnitsClassificationsLoading = true;
    },
    fetchUnitsClassificationsSuccess: (state) => {
      state.isUnitsClassificationsLoading = false;
    },
    fetchUnitsClassificationsError: (state) => {
      state.isUnitsClassificationsLoading = false;
    },

    setPointsByClassificationVisibility: (state, { payload }) => {
      state.classifications = {
        ...state.classifications,
        [payload.alias]: payload?.isActive
      };
    },
    setAllPointsByClassificationVisibility: (state, { payload }) => {
      const updatedClassifiedPointsState = Object.keys(
        state.classifications
      ).reduce((acc, key) => {
        acc[key] = payload?.isActive;
        return acc;
      }, {});

      state.classifications = updatedClassifiedPointsState as Record<
        PointClassificationsAliases,
        boolean
      >;
    },

    fetchPointsExcelRequest: (state) => {
      state.isExportPointsToExcelLoading = true;
    },
    fetchPointsExcelSuccess: (state) => {
      state.isExportPointsToExcelLoading = false;
    },
    fetchPointsExcelError: (state) => {
      state.isExportPointsToExcelLoading = false;
    },

    //Unit polygons operations[START]
    setPolygonUnitVisibility: (state, { payload }) => {
      state.unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units =
        state.unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units.map(
          (item) =>
            item.id.toString() === payload.id.toString()
              ? { ...item, isVisible: payload.isVisible }
              : item
        );
    },
    setAllPolygonsVisibility: (state, { payload }) => {
      state.unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units =
        state.unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units.map(
          (item) => ({ ...item, isVisible: payload.isVisible })
        );
    },
    updateCreationPolygonParameters: (state, { payload }) => {
      state.creationPolygonParameters = payload;
    },
    clearCreationPolygonParameters: (state) => {
      state.creationPolygonParameters = initialState.creationPolygonParameters;
    },
    updateCreationPolygonHistory: (state, { payload }) => {
      if (!state.isHistoryFrozen) state.creationPolygonHistory = payload;
    },
    returnToPreviousHistoryStep: (state) => {
      const backHistoryState = state.creationPolygonHistory.slice(0, -1);
      const isConditionToStopChangeHistory =
        backHistoryState[backHistoryState.length - 1]?.id !==
        state.creationPolygonParameters?.id;

      if (!state.isHistoryFrozen && !isConditionToStopChangeHistory) {
        state.creationPolygonHistory = backHistoryState;
        state.creationPolygonParameters =
          backHistoryState.length > 0
            ? backHistoryState[backHistoryState.length - 1]
            : initialState.creationPolygonParameters;
      }
    },
    clearCreationPolygonHistory: (state) => {
      state.creationPolygonHistory = initialState.creationPolygonHistory;
    },
    freezeHistory: (state) => {
      state.isHistoryFrozen = true;
    },
    unfreezeHistory: (state) => {
      state.isHistoryFrozen = false;
    },
    //Unit polygons operations[END]

    //Unit points operations[START]

    //Unit points operations[END]

    restoreDefaultState: () => initialState
  }
});

export const { actions, reducer } = mainSlice;
